// src/components/POS/Receipt/AppliedDiscountBadge.jsx
import React from 'react';
import { Tag, X, Percent, IndianRupee, Gift, Package, TrendingUp } from 'lucide-react';

/**
 * Get icon based on discount type
 */
const getDiscountIcon = (discountType) => {
  switch (discountType) {
    case 'percentage':
      return <Percent size={12} />;
    case 'fixed':
      return <IndianRupee size={12} />;
    case 'bogo':
    case 'complimentary':
      return <Gift size={12} />;
    case 'bundle':
      return <Package size={12} />;
    case 'tiered':
      return <TrendingUp size={12} />;
    default:
      return <Tag size={12} />;
  }
};

/**
 * Parse and sort tier slabs
 */
const parseTierSlabs = (tierConfig) => {
  if (!tierConfig) return [];
  try {
    const parsed = typeof tierConfig === 'string' ? JSON.parse(tierConfig) : tierConfig;
    return Array.isArray(parsed) ? parsed.sort((a, b) => (a.minAmount || 0) - (b.minAmount || 0)) : [];
  } catch { return []; }
};

/**
 * Find the active tier slab based on subtotal
 */
const getActiveTierLabel = (discount, discountAmount) => {
  const slabs = parseTierSlabs(discount.tierConfig);
  if (slabs.length === 0) return 'Tiered';
  // If we have a discount amount, reverse-calculate which slab matched
  if (discountAmount > 0) {
    // Find the slab whose percentage matches: discountAmount ≈ subtotal * value / 100
    // Since we don't have subtotal, show all slabs compactly
  }
  return slabs.map(s => `${s.value}%`).join(' / ');
};

/**
 * Format discount value for display
 */
const formatDiscountValue = (discount, discountAmount) => {
  const { discountType, discountValue, buyQuantity, getQuantity, bundlePrice } = discount;

  switch (discountType) {
    case 'percentage':
      return `${discountValue}%`;
    case 'fixed':
      return `₹${discountValue}`;
    case 'bogo':
      if (buyQuantity === 1 && getQuantity === 1) {
        return 'BOGO';
      }
      return `B${buyQuantity}G${getQuantity}`;
    case 'complimentary':
      return `B${buyQuantity || 1}G${getQuantity || 1}`;
    case 'bundle':
      return `₹${bundlePrice || 0}`;
    case 'tiered':
      return getActiveTierLabel(discount, discountAmount);
    default:
      return '';
  }
};

/**
 * AppliedDiscountBadge - Shows the currently applied discount with remove option
 * @param {Object} discount - The applied discount object
 * @param {number} discountAmount - Calculated discount amount
 * @param {Function} onRemove - Callback to remove the discount
 */
export default function AppliedDiscountBadge({ discount, discountAmount = 0, onRemove }) {
  if (!discount) return null;

  const { code, name, discountType } = discount;
  const tierSlabs = discountType === 'tiered' ? parseTierSlabs(discount.tierConfig) : [];

  return (
    <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg px-3 py-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {/* Icon */}
          <div className="w-6 h-6 rounded-full bg-green-600 text-white flex items-center justify-center flex-shrink-0">
            {getDiscountIcon(discountType)}
          </div>

          {/* Info */}
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-bold text-green-800 bg-green-200 px-1.5 py-0.5 rounded">
                {code}
              </span>
              <span className="text-xs text-green-700">
                {formatDiscountValue(discount, discountAmount)} off
              </span>
            </div>
            <p className="text-[10px] text-green-600 truncate max-w-[150px]">
              {name}
            </p>
          </div>
        </div>

      {/* Amount & Remove */}
      <div className="flex items-center gap-2">
        <div className="text-right">
          <div className="text-sm font-bold text-green-700">
            -₹{discountAmount.toFixed(2)}
          </div>
          <div className="text-[10px] text-green-600">Saving</div>
        </div>

        <button
          onClick={onRemove}
          className="p-1 hover:bg-red-100 rounded-full transition-colors group"
          title="Remove discount"
        >
          <X size={14} className="text-slate-400 group-hover:text-red-500" />
        </button>
      </div>
      </div>

      {/* Tier slabs detail */}
      {discountType === 'tiered' && tierSlabs.length > 0 && (
        <div className="mt-1.5 pt-1.5 border-t border-green-200 flex flex-wrap gap-1">
          {tierSlabs.map((slab, idx) => (
            <span key={idx} className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-700">
              ≥₹{slab.minAmount} → {slab.value}%
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
